import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, useAuth, API } from "@/lib/api";

import "@/styles/legal.css";

/**
 * Phase 7C+ — Modal d'acquittement bloquant.
 *
 * - Mounted at the App root for stagiaires & formateurs.
 * - Polls /api/legal/reglement_interieur/ack-status on auth.
 * - If mandatory && !acknowledged → shows a non-dismissable modal that scrolls
 *   the document and requires the user to scroll near the bottom + tick a
 *   checkbox before the "J'accepte" button is enabled.
 * - Hidden on the standalone /reglement-interieur page (so users can read alone).
 * - Admins are skipped — they manage docs via /admin/parametres.
 */
export default function LegalAckModal() {
    const { user } = useAuth();
    const location = useLocation();
    const [doc, setDoc] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Skip on standalone page or for admins / unauthenticated.
    // (axios `api` already auto-injects the bearer token from localStorage.)
    const skip = !user || user.role === "admin" || location.pathname === "/reglement-interieur";

    useEffect(() => {
        if (skip) { setOpen(false); return; }
        let alive = true;
        setLoading(true);
        api.get("/legal/reglement_interieur/ack-status")
            .then((r) => {
                if (!alive) return;
                if (r.data && r.data.exists && r.data.mandatory && !r.data.acknowledged) {
                    return api.get("/legal/reglement_interieur").then((rr) => {
                        if (!alive) return;
                        setDoc(rr.data);
                        setOpen(true);
                    });
                }
                setOpen(false);
            })
            .catch(() => {})
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [user, skip, location.pathname]);

    const onScroll = (e) => {
        const target = e.target;
        const reached = target.scrollHeight - target.scrollTop - target.clientHeight < 80;
        if (reached) setScrolled(true);
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post("/legal/reglement_interieur/acknowledge", { accepted: true });
            toast.success("Règlement intérieur accepté");
            setOpen(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const r = await fetch(`${API}/legal/reglement_interieur/pdf`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url;
            a.download = `reglement-interieur-v${doc?.version || "1.0"}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { /* noop */ }
    };

    if (!open || !doc) return null;

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-brand-ink/80 p-4 backdrop-blur-sm" data-testid="legal-ack-modal">
            <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-premium-lg"
            >
                {/* Header */}
                <div className="flex items-center gap-3 border-b border-brand-line bg-brand-sand px-8 py-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                        <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-brand-orange">Action requise · Qualiopi</div>
                        <h3 className="font-display text-xl">Règlement intérieur — version {doc.version}</h3>
                        <p className="text-xs text-brand-smoke">Lecture complète et acceptation explicite requises pour continuer à utiliser la plateforme.</p>
                    </div>
                    <button
                        type="button"
                        onClick={downloadPdf}
                        data-testid="legal-ack-pdf"
                        className="inline-flex items-center gap-1 rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs hover:border-brand-orange"
                    >
                        <FileDown className="h-3 w-3" /> PDF
                    </button>
                </div>

                {/* Body */}
                <div
                    className="flex-1 overflow-y-auto px-8 py-6"
                    onScroll={onScroll}
                    data-testid="legal-ack-body"
                >
                    <div dangerouslySetInnerHTML={{ __html: doc.html }} />
                </div>

                {/* Footer */}
                <div className="border-t border-brand-line bg-white px-8 py-4">
                    {!scrolled && (
                        <p className="mb-3 text-center text-xs text-brand-smoke" data-testid="legal-ack-scroll-hint">
                            Veuillez faire défiler le document jusqu'à la fin avant de pouvoir accepter.
                        </p>
                    )}
                    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                        scrolled ? "border-brand-line bg-white hover:border-brand-orange" : "border-brand-line/60 bg-[#FAFAFA] opacity-60 cursor-not-allowed"
                    }`}>
                        <input
                            type="checkbox"
                            disabled={!scrolled}
                            checked={accepted}
                            onChange={(e) => setAccepted(e.target.checked)}
                            data-testid="legal-ack-checkbox"
                            className="mt-1 h-4 w-4 accent-brand-orange"
                        />
                        <span className="text-sm text-brand-ink">
                            J'ai lu et j'accepte le règlement intérieur de Cursus Physio dans sa version <strong>{doc.version}</strong>.
                            Je comprends que mon acceptation est horodatée et conservée à titre de preuve dans le cadre de la conformité Qualiopi.
                        </span>
                    </label>
                    <div className="mt-4 flex items-center justify-end">
                        <button
                            onClick={submit}
                            disabled={!accepted || !scrolled || submitting}
                            data-testid="legal-ack-submit"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {submitting ? "Enregistrement…" : "J'accepte et continue"}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
